import { lazy, Suspense, useEffect, useState } from 'react'
import { animationCache, DEFAULT_ASPECT_RATIO_CLASSNAME } from './LottiePlayer.constants'
import type { LottieAnimationData, LottiePlayerProps } from './LottiePlayer.types'

const loadLottieChunk = () => import('lottie-react')
const Lottie = lazy(loadLottieChunk)

export default function LottiePlayer({
  src,
  loop = true,
  autoplay = true,
  className,
  aspectRatioClassName = DEFAULT_ASPECT_RATIO_CLASSNAME,
  ariaLabel,
  fallback = null,
}: LottiePlayerProps) {
  const [animationData, setAnimationData] = useState<LottieAnimationData | null>(
    () => animationCache.get(src) ?? null
  )

  useEffect(() => {
    if (animationCache.has(src)) {
      setAnimationData(animationCache.get(src) ?? null)
      return
    }

    let cancelled = false
    const controller = new AbortController()

    void loadLottieChunk()

    const loadAnimation = async () => {
      try {
        const response = await fetch(src, { signal: controller.signal })
        if (!response.ok) return
        const data = (await response.json()) as LottieAnimationData
        animationCache.set(src, data)
        if (!cancelled) setAnimationData(data)
      } catch {
        // network / parse errors are swallowed; caller can show `fallback`.
      }
    }

    loadAnimation()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [src])

  const wrapperClassName = [aspectRatioClassName, className].filter(Boolean).join(' ')

  return (
    <div className={wrapperClassName} aria-label={ariaLabel} role={ariaLabel ? 'img' : undefined}>
      {animationData ? (
        <Suspense fallback={<div className="h-full w-full">{fallback}</div>}>
          <Lottie
            animationData={animationData}
            loop={loop}
            autoplay={autoplay}
            className="h-full w-full"
          />
        </Suspense>
      ) : (
        <div className="h-full w-full">{fallback}</div>
      )}
    </div>
  )
}
